import React, { useState } from "react";
import {
	SOCIAL_PLATFORMS,
	SocialPlatformKey,
} from "../../../constants/socialPlatforms";

interface Step1Props {
	formData: {
		organizerType: string;
		organizerName: string;
		representativeName: string;
		email: string;
		phone: string;
		website: string;
		description: string;
	};
	socialLinks: Record<SocialPlatformKey, string>;
	handleChange: (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>
	) => void;
	onSocialLinkChange: (platform: SocialPlatformKey, value: string) => void;
	nextStep: () => void;
}

const Step1: React.FC<Step1Props> = ({
	formData,
	socialLinks,
	handleChange,
	onSocialLinkChange,
	nextStep,
}) => {
	const [errors, setErrors] = useState({
		organizerType: "",
		organizerName: "",
		representativeName: "",
		email: "",
		phone: "",
	});

	const handleNextStep = () => {
		const newErrors = {
			organizerType: "",
			organizerName: "",
			representativeName: "",
			email: "",
			phone: "",
		};

		// 必須フィールドのチェック
		if (!formData.organizerType) {
			newErrors.organizerType = "主催者タイプを選択してください";
		}
		if (!formData.organizerName) {
			newErrors.organizerName = "名称を入力してください";
		}
		if (!formData.representativeName) {
			newErrors.representativeName = "代表者氏名を入力してください";
		}
		if (!formData.email) {
			newErrors.email = "メールアドレスを入力してください";
		} else {
			// メールアドレスのフォーマットチェック
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(formData.email)) {
				newErrors.email = "正しいメールアドレス形式で入力してください";
			}
		}
		if (!formData.phone) {
			newErrors.phone = "電話番号を入力してください";
		} else {
			// 電話番号のフォーマットチェック（数字とハイフンのみ）
			const phoneRegex = /^[0-9-]+$/;
			if (!phoneRegex.test(formData.phone)) {
				newErrors.phone = "電話番号は数字とハイフンで入力してください";
			}
		}

		setErrors(newErrors);

		// エラーがある場合は進まない
		if (Object.values(newErrors).some((error) => error !== "")) {
			alert("入力内容に誤りがあります。赤く表示された項目を確認してください。");
			return;
		}

		nextStep();
	};

	return (
		<div className="space-y-6">
			<div className="mb-6">
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					主催者タイプ <span className="text-red-600 dark:text-red-400">*</span>
				</label>
				<fieldset className="flex flex-wrap gap-3">
					<legend className="sr-only">主催者タイプ</legend>

					<div>
						<label
							htmlFor="organizerType-artist"
							className={`flex cursor-pointer items-center justify-center rounded-md border ${
								errors.organizerType
									? "border-red-500 dark:border-red-500"
									: "border-gray-200 dark:border-gray-700"
							} bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white hover:border-gray-300 dark:hover:border-gray-600 has-[:checked]:border-violet-600 has-[:checked]:bg-gradient-to-r has-[:checked]:from-violet-500 has-[:checked]:to-purple-600 has-[:checked]:text-white transition-all`}
						>
							<input
								type="radio"
								name="organizerType"
								value="artist"
								id="organizerType-artist"
								className="sr-only"
								checked={formData.organizerType === "artist"}
								onChange={(e) => {
									handleChange(e);
									setErrors((prev) => ({ ...prev, organizerType: "" }));
								}}
								required
							/>
							<p className="text-sm font-medium">アーティスト</p>
						</label>
					</div>

					<div>
						<label
							htmlFor="organizerType-company"
							className={`flex cursor-pointer items-center justify-center rounded-md border ${
								errors.organizerType
									? "border-red-500 dark:border-red-500"
									: "border-gray-200 dark:border-gray-700"
							} bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white hover:border-gray-300 dark:hover:border-gray-600 has-[:checked]:border-violet-600 has-[:checked]:bg-gradient-to-r has-[:checked]:from-violet-500 has-[:checked]:to-purple-600 has-[:checked]:text-white transition-all`}
						>
							<input
								type="radio"
								name="organizerType"
								value="company"
								id="organizerType-company"
								className="sr-only"
								checked={formData.organizerType === "company"}
								onChange={(e) => {
									handleChange(e);
									setErrors((prev) => ({ ...prev, organizerType: "" }));
								}}
								required
							/>
							<p className="text-sm font-medium">会社・団体</p>
						</label>
					</div>

					<div>
						<label
							htmlFor="organizerType-individual"
							className={`flex cursor-pointer items-center justify-center rounded-md border ${
								errors.organizerType
									? "border-red-500 dark:border-red-500"
									: "border-gray-200 dark:border-gray-700"
							} bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white hover:border-gray-300 dark:hover:border-gray-600 has-[:checked]:border-violet-600 has-[:checked]:bg-gradient-to-r has-[:checked]:from-violet-500 has-[:checked]:to-purple-600 has-[:checked]:text-white transition-all`}
						>
							<input
								type="radio"
								name="organizerType"
								value="individual"
								id="organizerType-individual"
								className="sr-only"
								checked={formData.organizerType === "individual"}
								onChange={(e) => {
									handleChange(e);
									setErrors((prev) => ({ ...prev, organizerType: "" }));
								}}
								required
							/>
							<p className="text-sm font-medium">個人</p>
						</label>
					</div>
				</fieldset>
				{errors.organizerType && (
					<p className="mt-2 text-xs text-red-600 dark:text-red-400">
						{errors.organizerType}
					</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					{formData.organizerType === "artist"
						? "アーティスト名"
						: formData.organizerType === "company"
						? "会社・団体名"
						: "主催者名"}{" "}
					<span className="text-red-600 dark:text-red-400">*</span>
				</label>
				<input
					type="text"
					name="organizerName"
					value={formData.organizerName}
					onChange={(e) => {
						handleChange(e);
						setErrors((prev) => ({ ...prev, organizerName: "" }));
					}}
					className={`w-full px-3 sm:px-4 py-2 border ${
						errors.organizerName
							? "border-red-500 dark:border-red-500"
							: "border-gray-200 dark:border-gray-700"
					} bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400`}
					required
				/>
				{errors.organizerName && (
					<p className="mt-1 text-xs text-red-600 dark:text-red-400">
						{errors.organizerName}
					</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					代表者氏名 <span className="text-red-600 dark:text-red-400">*</span>
				</label>
				<input
					type="text"
					name="representativeName"
					value={formData.representativeName}
					onChange={(e) => {
						handleChange(e);
						setErrors((prev) => ({ ...prev, representativeName: "" }));
					}}
					className={`w-full px-3 sm:px-4 py-2 border ${
						errors.representativeName
							? "border-red-500 dark:border-red-500"
							: "border-gray-200 dark:border-gray-700"
					} bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400`}
					required
				/>
				{errors.representativeName && (
					<p className="mt-1 text-xs text-red-600 dark:text-red-400">
						{errors.representativeName}
					</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					連絡先メールアドレス{" "}
					<span className="text-red-600 dark:text-red-400">*</span>
				</label>
				<input
					type="email"
					name="email"
					value={formData.email}
					onChange={(e) => {
						handleChange(e);
						setErrors((prev) => ({ ...prev, email: "" }));
					}}
					className={`w-full px-3 sm:px-4 py-2 border ${
						errors.email
							? "border-red-500 dark:border-red-500"
							: "border-gray-200 dark:border-gray-700"
					} bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400`}
					required
				/>
				{errors.email && (
					<p className="mt-1 text-xs text-red-600 dark:text-red-400">
						{errors.email}
					</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					電話番号 <span className="text-red-600 dark:text-red-400">*</span>
				</label>
				<input
					type="tel"
					name="phone"
					value={formData.phone}
					onChange={(e) => {
						handleChange(e);
						setErrors((prev) => ({ ...prev, phone: "" }));
					}}
					placeholder="09012345678 または 090-1234-5678"
					className={`w-full px-3 sm:px-4 py-2 border ${
						errors.phone
							? "border-red-500 dark:border-red-500"
							: "border-gray-200 dark:border-gray-700"
					} bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400`}
					required
				/>
				{errors.phone && (
					<p className="mt-1 text-xs text-red-600 dark:text-red-400">
						{errors.phone}
					</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					ウェブサイト
				</label>
				<input
					type="url"
					name="website"
					value={formData.website}
					onChange={handleChange}
					className="w-full px-3 sm:px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400"
					placeholder="https://"
				/>
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					プロフィール・説明
				</label>
				<textarea
					name="description"
					value={formData.description}
					onChange={handleChange}
					rows={4}
					className="w-full px-3 sm:px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400"
					placeholder="主催するイベントやアーティストについての説明"
				/>
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					公式SNS（任意）
				</label>
				<p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
					アカウントID（@example など）または URL
					を入力すると、自動でリンクに変換されます。
				</p>
				<div className="grid gap-4 md:grid-cols-2">
					{SOCIAL_PLATFORMS.map((platform) => (
						<div key={platform.key} className="space-y-1">
							<div className="text-sm font-medium text-gray-600 dark:text-gray-400">
								{platform.label}
							</div>
							<input
								type="text"
								value={socialLinks[platform.key]}
								onChange={(e) =>
									onSocialLinkChange(platform.key, e.target.value)
								}
								className="w-full px-3 sm:px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400"
								placeholder={platform.placeholder}
							/>
						</div>
					))}
				</div>
			</div>

			<div className="pt-4">
				<button
					type="button"
					onClick={handleNextStep}
					className="w-full bg-gradient-to-r from-violet-500 to-purple-600 text-white py-3 rounded-lg font-bold hover:from-violet-600 hover:to-purple-700 transition-all"
				>
					次へ
				</button>
			</div>
		</div>
	);
};

export default Step1;
